import { getFirestore, collection, query, where, getDocs, getCountFromServer, getAggregateFromServer, sum, orderBy } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { locale } from "./LocaleService.js";

const db = getFirestore(app);

export const DashboardService = {
    getAuthorStats: async (uid) => {
        try {
            // 1. Fetch Follower Count (From User Profile)
            let followers = 0;
            // Assuming we have this in window.user for fast access, or fetch from 'users' collection
            // The structure might be 'followers' subcollection or a count field.
            // Let's assume 'followerCount' field in user doc based on previous context.
            // If not, we count 'users/{uid}/followers'
            const userRef = collection(db, "users"); // Actually doc, but let's query series first.

            // To get accurate follower count, we need to count the 'followers' subcollection.
            try {
                const userFollowersRef = collection(db, "users", uid, "followers");
                const followerSnap = await getCountFromServer(userFollowersRef);
                followers = followerSnap.data().count;
            } catch (err) {
                console.warn("Follower Count Error", err);
                // Fallback to window.user if available
                if (window.user && window.user.uid === uid) {
                    followers = window.user.followerCount || 0;
                }
            }

            // 2. Fetch All Series
            const q = query(
                collection(db, "series"),
                where("authorId", "==", uid),
                orderBy("createdAt", "desc")
            );
            const snapshot = await getDocs(q);

            let totalViews = 0;
            let totalLikes = 0;
            const seriesList = [];

            snapshot.forEach(doc => {
                const data = doc.data();
                const stats = data.stats || {};
                totalViews += (stats.viewCount || 0);
                totalLikes += (stats.likeCount || 0);

                seriesList.push({
                    id: doc.id,
                    ...data
                });
            });

            // 3. Real Chart Data (Weekly)
            const today = new Date();
            const last7Days = [];
            const labels = [];

            // Generate labels for the last 7 days (e.g., "MM/DD")
            for (let i = 6; i >= 0; i--) {
                const d = new Date(today);
                d.setDate(today.getDate() - i);
                const dateStr = d.toISOString().split('T')[0]; // YYYY-MM-DD
                last7Days.push(dateStr);

                // Label Format: MM/DD (Localized)
                labels.push(locale.formatDate(d, { month: 'numeric', day: 'numeric' }));
            }

            // IMPORTANT: Firestore range queries on string dates work (lexicographical), provided format is YYYY-MM-DD.
            // We need a composite index on (authorId, date) for efficient querying, or just fetch recent docs.
            // Since we don't have many docs yet, we can query by authorId and client-side filter or use range.
            // Let's try range query. Requires Index. If index fails, we catch and fallback or user creates it.

            // Query daily_stats for this author
            // SIMPLIFIED: Query ONLY by authorId to avoid composite index requirement.
            // Filter by date on client-side (low volume).

            const statsQuery = query(
                collection(db, "daily_stats"),
                where("authorId", "==", uid)
                // Remove orderBy and date filter from query to prevent "Index Required" error
            );

            let clickData = new Array(7).fill(0); // 0 to 6
            const startDateStr = last7Days[0]; // Oldest date we care about

            try {
                const statsSnapshot = await getDocs(statsQuery);
                statsSnapshot.forEach(doc => {
                    const data = doc.data();
                    // Client-Side Filter
                    if (data.date >= startDateStr) {
                        const index = last7Days.indexOf(data.date);
                        if (index !== -1) {
                            clickData[index] += (data.viewCount || 0);
                        }
                    }
                });
            } catch (queryError) {
                console.warn("Stats Query Error:", queryError);
                // Graceful degradation: all 0s
            }

            const chartData = {
                labels: labels,
                datasets: [{
                    label: '일별 조회수',
                    data: clickData,
                    borderColor: '#9333ea', // Purple-600
                    backgroundColor: 'rgba(147, 51, 234, 0.2)',
                    tension: 0.4,
                    fill: true
                }]
            };

            return {
                summary: {
                    totalViews,
                    totalLikes,
                    totalSeries: snapshot.size,
                    totalFollowers: followers
                },
                seriesList,
                chartData
            };

        } catch (e) {
            console.error("Dashboard Stats Error:", e);
            throw e;
        }
    }
};
